import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import useMediaQuery from '@mui/material/useMediaQuery';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import { forwardRef, PropsWithChildren, ReactElement, Ref } from 'react';
import { CircularProgress, Slide, useTheme, Zoom, Box, Typography, IconButton } from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import { Close, TaskAlt } from '@mui/icons-material';

export type ConfirmationModalProps = PropsWithChildren<{
  isOpen: boolean;
  onClose?: () => void;
  onConfirm?: () => void;
  title?: string;
  content?: string;
  confirmButtonText?: string;
  closeButtonText?: string;
  isLoading: boolean;
  isLogin?: boolean;
  onCloseLogin?: () => void;
  /** Fixed CSS max-width (e.g. `580px`) or `auto` to size to content (capped at 90vw). */
  optionalWidth?: string;
  confirmButtonDisabled?: boolean;
  hideCloseButton?: boolean;
  customHeaderStyle?: {
    background?: string;
    titleColor?: string;
    icon?: React.ReactNode;
    buttonColor?: string;
  };
}>;
const Transition = forwardRef(function Transition(
  props: TransitionProps & { children: ReactElement },
  ref: Ref<unknown>
) {
  return <Slide direction="down" ref={ref} {...props} />;
});

const defaultConfirmHeaderIcon = (
  <Box
    sx={{
      width: 32,
      height: 32,
      borderRadius: 1,
      bgcolor: 'rgba(255,255,255,0.2)',
      color: 'white',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <TaskAlt sx={{ fontSize: 18 }} />
  </Box>
);

export const ConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  children,
  title,
  content,
  confirmButtonText,
  closeButtonText,
  isLoading,
  isLogin,
  onCloseLogin,
  optionalWidth,
  confirmButtonDisabled,
  hideCloseButton,
  customHeaderStyle,
}: ConfirmationModalProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isAutoWidth = optionalWidth === 'auto';
  const paperWidthSx = isMobile
    ? {
        width: 'calc(100% - 32px)',
        maxWidth: 'calc(100% - 32px)',
        margin: '16px',
      }
    : isAutoWidth
      ? { width: 'fit-content', maxWidth: 'min(90vw, 960px)' }
      : { width: '100%', maxWidth: optionalWidth ?? '500px' };

  const headerBackground =
    customHeaderStyle?.background ?? 'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)';
  const headerTitleColor = customHeaderStyle?.titleColor ?? 'white';
  const headerIcon =
    customHeaderStyle !== undefined ? customHeaderStyle.icon : defaultConfirmHeaderIcon;

  return (
    <Dialog
      TransitionComponent={Transition}
      open={isOpen}
      onClose={undefined}
      fullScreen={false}
      disableEscapeKeyDown={true}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      PaperProps={{
        sx: {
          ...paperWidthSx,
          maxHeight: '90vh',
          zIndex: 99999999,
          backgroundColor: theme.palette.grey[50],
          borderRadius: isMobile ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Box
        sx={{
          background: headerBackground,
          padding: { xs: '12px 16px', sm: '16px 20px' },
          borderRadius: '4px 4px 0 0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            zIndex: 0,
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, position: 'relative', zIndex: 1, minWidth: 0 }}>
          {headerIcon ? <Box sx={{ flexShrink: 0 }}>{headerIcon}</Box> : null}
          <Typography
            variant="h6"
            sx={{
              color: headerTitleColor,
              fontWeight: 700,
              fontSize: '1rem !important',
              textShadow: '0 1px 3px rgba(0,0,0,0.3)',
            }}
          >
            {title}
          </Typography>
        </Box>
        {!hideCloseButton && (
          <IconButton
            onClick={onClose}
            sx={{
              color: headerTitleColor,
              position: 'relative',
              zIndex: 1,
              flexShrink: 0,
              width: 36,
              height: 36,
              border: '1px solid',
              borderColor: 'rgba(255,255,255,0.3)',
              backgroundColor: 'rgba(255,255,255,0.1)',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.5)',
                transform: 'rotate(90deg)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        )}
      </Box>
      <Zoom in={true} timeout={500}>
        <DialogContent sx={{ m: 0, px: { xs: 2, sm: 3 }, py: 2, '&.MuiDialogContent-root': { m: 0 } }}>
          {content && (
            <DialogContentText sx={{ m: 0, mb: 1, color: 'black' }} id="alert-dialog-description">
              {content}
            </DialogContentText>
          )}
          {children}
        </DialogContent>
      </Zoom>
      <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}>
        <Button
          onClick={onClose}
          sx={{
            color: confirmButtonText === 'Sil' ? `#dc2626 !important` : '#0b6bcb !important',
            border: confirmButtonText === 'Sil' ? `1px solid #dc2626` : '1px solid #0b6bcb',
            display: closeButtonText === '' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.875rem',
            height: '30px',
            padding: '4px 16px',
          }}
        >
          {closeButtonText || 'Vazgeç'}
        </Button>
        <Button
          disabled={isLoading || confirmButtonDisabled}
          sx={{
            color: 'white !important',
            height: '30px',
            minWidth: '120px',
            maxWidth: '300px',
            padding: '4px 16px !important',
            fontSize: '0.875rem',
            display: confirmButtonText === '' ? 'none' : 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            whiteSpace: 'nowrap',
            background:
              (confirmButtonText === 'Sil'
                ? 'linear-gradient(135deg,#dc2626 0%, #b91c1c 100%)'
                : customHeaderStyle?.buttonColor) ||
              'linear-gradient(135deg, #1565c0 0%, #0d47a1 100%)',
            '&:hover': {
              background: customHeaderStyle?.buttonColor
                ? `linear-gradient(0deg, rgba(0,0,0,0.2), rgba(0,0,0,0.2)), ${customHeaderStyle.buttonColor}`
                : 'linear-gradient(135deg, #0d47a1 0%, #0a3d91 100%)',
              color: 'white',
            },
            '&.Mui-disabled': {
              background: customHeaderStyle?.buttonColor
                ? `${customHeaderStyle.buttonColor}`
                : 'linear-gradient(135deg, rgba(21,101,192,0.5) 0%, rgba(13,71,161,0.5) 100%)',
              color: 'white !important',
            },
          }}
          onClick={onConfirm}
          autoFocus
        >
          {!isLoading ? (
            confirmButtonText || 'Onayla'
          ) : (
            <CircularProgress sx={{ width: '20px !important', height: '20px !important', color: 'white' }} />
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
